/**
 * The transport layer: one fetch wrapper, one error type, one place that knows where the API is.
 *
 * Ported from `lib/apiClient.ts` and `lib/apiUrl.ts` in the Next.js app, and deliberately kept
 * as thin as it was there. The interesting behaviour is in two places, both of which are
 * security- or correctness-relevant rather than cosmetic:
 *
 * - **Only the `{ "error": string }` envelope is trusted for display.** A non-JSON body is kept
 *   on the error for diagnostics but never echoed: a 502 from a proxy arrives as a whole HTML
 *   document, and rendering that as a message puts markup-as-text in front of a user.
 * - **The status is preserved**, because the UI branches on it. A 403 is "an admin has to
 *   authorise you" and must never be presented as a generic failure with a retry button —
 *   retrying will never succeed. That instruction was written against this rewrite in
 *   `lib/api/auth.ts`; this is where it gets honoured.
 *
 * Unlike the Next.js version there is no same-origin fallback. This app is always served from a
 * different origin to the API, so the base URL is required rather than optional, and a missing
 * one is a startup error rather than a silent request to the wrong place.
 */

/** Non-2xx response from the API, with the status and parsed body attached. */
export class ApiClientError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = "ApiClientError";
    this.status = status;
    this.body = body;
  }

  /** The caller is signed in but not authorised — an admin has to allowlist them. */
  get isNotAuthorised(): boolean {
    return this.status === 403;
  }

  /** The credential is no longer good. The only useful response is to sign in again. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

/**
 * A defensive ceiling on an echoed message. The API writes one sentence; anything far longer
 * did not come from its error funnel and has no business being rendered.
 */
const MAX_ECHOED_MESSAGE_CHARS = 300;

/** The version prefix the backend mounts everything under. */
const API_VERSION_PREFIX = "/api/v1";

function resolveBaseUrl(): string {
  const configured = import.meta.env.VITE_API_BASE_URL?.trim();
  if (!configured) {
    throw new Error(
      "VITE_API_BASE_URL is not set. This app is served from a different origin to its API, " +
        "so there is no same-origin fallback — see frontend/.env.example.",
    );
  }
  return configured.replace(/\/+$/, "");
}

/** The absolute URL for an API path. `path` is everything after the version prefix. */
export function apiUrl(path: string): string {
  if (!path.startsWith("/")) throw new Error(`apiUrl: path must start with "/", got "${path}"`);
  return `${resolveBaseUrl()}${API_VERSION_PREFIX}${path}`;
}

export interface RequestOptions {
  method?: string;
  /** Serialised as JSON. Use `formData` instead for the edit route's multipart upload. */
  body?: unknown;
  formData?: FormData;
  /** Firebase ID token; the header is omitted entirely when null. */
  idToken: string | null;
  signal?: AbortSignal;
}

export async function request<T>(path: string, options: RequestOptions): Promise<T> {
  const { method = "GET", body, formData, idToken, signal } = options;

  const headers: Record<string, string> = {};
  if (idToken) headers.Authorization = `Bearer ${idToken}`;
  // **No `Content-Type` for multipart**: the browser must set it so the boundary is generated.
  if (formData === undefined && body !== undefined) headers["Content-Type"] = "application/json";

  const response = await fetch(apiUrl(path), {
    method,
    headers,
    body: formData ?? (body === undefined ? undefined : JSON.stringify(body)),
    signal,
  });

  return handleResponse<T>(response);
}

/** The bytes of a file-serving endpoint, as a blob. Used by the download and export routes. */
export async function requestBlob(path: string, idToken: string | null): Promise<Response> {
  const headers: Record<string, string> = {};
  if (idToken) headers.Authorization = `Bearer ${idToken}`;

  const response = await fetch(apiUrl(path), { headers });
  if (!response.ok) {
    // Read the body as text so a JSON error envelope still produces a real message, rather
    // than "Download failed with status 403" for a case the user can act on.
    const payload = parseIfJson(await response.text());
    throw new ApiClientError(response.status, extractMessage(payload, response), payload);
  }
  return response;
}

async function handleResponse<T>(response: Response): Promise<T> {
  const payload = await readBody(response);
  if (!response.ok) {
    throw new ApiClientError(response.status, extractMessage(payload, response), payload);
  }
  return payload as T;
}

/** Parses JSON when present; tolerates 204s and non-JSON error pages. */
async function readBody(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? parseIfJson(text) : null;
}

function parseIfJson(text: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

function extractMessage(payload: unknown, response: Response): string {
  if (typeof payload === "object" && payload !== null) {
    const { error } = payload as { error?: unknown };
    if (typeof error === "string" && error.trim() && error.length <= MAX_ECHOED_MESSAGE_CHARS) {
      return error;
    }
  }
  return `Request failed with status ${response.status}`;
}

/**
 * The `details.kind` an image error carries, when it carries one.
 *
 * Three distinct causes reach the UI as a 4xx and they need different words: the provider's own
 * content policy refused (`moderation`), this app's safety filter refused (`safety`), or
 * something else went wrong (`generic`). Collapsing them would send a user round a loop that
 * cannot succeed.
 */
export type ImageErrorKind = "moderation" | "safety" | "generic";

export function imageErrorKind(error: unknown): ImageErrorKind | null {
  if (!(error instanceof ApiClientError)) return null;
  const body = error.body;
  if (typeof body !== "object" || body === null) return null;
  const details = (body as { details?: unknown }).details;
  if (typeof details !== "object" || details === null) return null;
  const kind = (details as { kind?: unknown }).kind;
  return kind === "moderation" || kind === "safety" || kind === "generic" ? kind : null;
}
